// momentDe.ts — DIE zentrale moment-Locale-Quelle der App (Termine/Kalender SP1).
//
// Hintergrund (Bug „Wochenansicht zeigt am Sonntag nur den Sonntag", Review
// 2026-07-05): react-big-calendar berechnet die Wochen-Range über
// `moment(date).startOf('week')` — also über die GLOBALE moment-Locale
// (das `firstOfWeek`-Argument wird vom moment-Localizer ignoriert,
// react-big-calendar/lib/localizers/moment.js `startOf`). Fehlt die
// de-LocaleData zur Laufzeit (Bundling-/Ladepfad), fällt moment auf 'en'
// zurück: Wochenstart Sonntag, „July", englische Kürzel. Der Event-Load
// (`computeVisibleRange`) rechnet dagegen fest `isoWeek` (Mo–So) → Grid und
// geladene Daten zeigen VERSCHIEDENE Wochen; nur die Sonntag-Spalte ist gefüllt.
//
// Nachtrag (Review 2026-07-07): Genau dieser Degradationspfad trat auf PROD/DEV
// ein — die Wochenregeln waren repariert, aber alle NAMEN blieben englisch
// („Mon/Tue/Wed 06.07.", „7. July 2026", „Wednesday" bei TWV/Frist). Deshalb
// bettet dieses Modul jetzt die KOMPLETTE deutsche Locale hart ein
// (1:1 gespiegelt aus moment/locale/de.js, moment 2.30.1) statt nur die
// Wochenregeln zu erzwingen — es gibt keinen Degradationspfad mehr:
//  1. registriert die offizielle de-Locale (Side-Effect-Import, Normalfall),
//  2. definiert via `updateLocale` die vollen deutschen Namen + Formate +
//     Wochenregeln (dow=1 Montag, doy=4 ISO-8601-KW) — `updateLocale` LEGT die
//     Locale notfalls neu an, d. h. selbst wenn (1) im Bundle je verloren geht,
//     bleiben Monats-/Tagesnamen, Datumsformate UND Wochenstart deutsch,
//  3. setzt die globale Locale auf 'de'.
//
// Verwendung: `import "./utils/momentDe";` als ERSTER Import in index.tsx
// (app-weit, bevor irgendein Consumer moment benutzt) + in jedem Modul, das
// bisher selbst `import "moment/locale/de"` + `moment.locale("de")` streute —
// EINE Pflegestelle statt verteilter Doppelpflege.

import moment from "moment";
import "moment/locale/de";

// 1:1 aus moment/locale/de.js: Deutsch braucht für relative Zeiten nach
// „vor/in" den Dativ („vor einem Tag") und ohne Suffix den Nominativ
// („ein Tag") — deshalb sind m/h/d/dd/w/M/MM/y/yy dort Funktionen.
function processRelativeTime(number: number, withoutSuffix: boolean, key: string): string {
    const format: Record<string, [string, string]> = {
        m: ["eine Minute", "einer Minute"],
        h: ["eine Stunde", "einer Stunde"],
        d: ["ein Tag", "einem Tag"],
        dd: [number + " Tage", number + " Tagen"],
        w: ["eine Woche", "einer Woche"],
        M: ["ein Monat", "einem Monat"],
        MM: [number + " Monate", number + " Monaten"],
        y: ["ein Jahr", "einem Jahr"],
        yy: [number + " Jahre", number + " Jahren"],
    };
    return withoutSuffix ? format[key][0] : format[key][1];
}

/**
 * Idempotent: stellt sicher, dass die globale moment-Locale 'de' ist — mit
 * vollständig eingebetteten deutschen Namen/Formaten und Montag-Wochenstart
 * (ISO-KW-Regel). Kann jederzeit erneut aufgerufen werden — z. B. als
 * defensiver Guard vor dem Erzeugen des react-big-calendar-Localizers oder
 * nach einer fremden Locale-Umschaltung.
 */
export function ensureMomentDe(): void {
    // Diagnose: Kam die offizielle de-LocaleData (Side-Effect-Import) im Bundle
    // an? Funktional ist das seit der Namens-Einbettung unten egal (updateLocale
    // legt 'de' notfalls komplett neu an) — das Log hilft aber, einen kaputten
    // Locale-Ladepfad im Bundling früh zu erkennen.
    if (!moment.locales().includes("de")) {
        // eslint-disable-next-line no-console
        console.warn(
            "[momentDe] de-LocaleData (Side-Effect-Import) fehlt zur Laufzeit — " +
                "die eingebettete deutsche Locale (Namen, Formate, Montag-Start) " +
                "wird trotzdem vollständig via updateLocale definiert."
        );
    }

    // Volle deutsche Locale HART eingebettet (Namen, Formate, Wochenregeln) —
    // 1:1 gespiegelt aus moment/locale/de.js. Existiert 'de' bereits
    // (Normalfall), überschreiben identische Werte (idempotent); fehlt die
    // LocaleData, definiert updateLocale die Locale hiermit vollständig.
    // → Deterministisch deutsch in JEDEM Ladepfad.
    moment.updateLocale("de", {
        months: [
            "Januar",
            "Februar",
            "März",
            "April",
            "Mai",
            "Juni",
            "Juli",
            "August",
            "September",
            "Oktober",
            "November",
            "Dezember",
        ],
        monthsShort: [
            "Jan.",
            "Feb.",
            "März",
            "Apr.",
            "Mai",
            "Juni",
            "Juli",
            "Aug.",
            "Sep.",
            "Okt.",
            "Nov.",
            "Dez.",
        ],
        monthsParseExact: true,
        weekdays: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
        weekdaysShort: ["So.", "Mo.", "Di.", "Mi.", "Do.", "Fr.", "Sa."],
        weekdaysMin: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
        weekdaysParseExact: true,
        longDateFormat: {
            LT: "HH:mm",
            LTS: "HH:mm:ss",
            L: "DD.MM.YYYY",
            LL: "D. MMMM YYYY",
            LLL: "D. MMMM YYYY HH:mm",
            LLLL: "dddd, D. MMMM YYYY HH:mm",
        },
        calendar: {
            sameDay: "[heute um] LT [Uhr]",
            sameElse: "L",
            nextDay: "[morgen um] LT [Uhr]",
            nextWeek: "dddd [um] LT [Uhr]",
            lastDay: "[gestern um] LT [Uhr]",
            lastWeek: "[letzten] dddd [um] LT [Uhr]",
        },
        relativeTime: {
            future: "in %s",
            past: "vor %s",
            s: "ein paar Sekunden",
            ss: "%d Sekunden",
            m: processRelativeTime,
            mm: "%d Minuten",
            h: processRelativeTime,
            hh: "%d Stunden",
            d: processRelativeTime,
            dd: processRelativeTime,
            w: processRelativeTime,
            ww: "%d Wochen",
            M: processRelativeTime,
            MM: processRelativeTime,
            y: processRelativeTime,
            yy: processRelativeTime,
        },
        dayOfMonthOrdinalParse: /\d{1,2}\./,
        // de.js nutzt das String-Template '%d.' — die moment-Typings erlauben in
        // LocaleSpecification nur die Funktionsform; Ergebnis ist identisch.
        ordinal: (n: number) => `${n}.`,
        // Harte Wochenregeln: dow=1 (Montag), doy=4 (ISO-8601-Kalenderwoche).
        week: { dow: 1, doy: 4 },
    });
    moment.locale("de");
}

ensureMomentDe();
